// src/components/ChessBenefits.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Container, Row, Col, Button, Badge } from "react-bootstrap";

export default function ChessBenefits() {
  // THEME
  const colors = {
    bg: "#0f0f0f",
    panel: "#151515",
    border: "#ff7a18",
    text: "#e6e6e6",
    mut: "#d8d8d8",
    dim: "#b7b7b7",
    chip: "#1b1b1b",
    chipBorder: "#2a2a2a",
    link: "#ffc896",
  };

  const shell = {
    background: colors.bg,
    border: `1px solid ${colors.border}`,
    borderRadius: 14,
    color: colors.text,
    padding: 18,
    maxWidth: 1200,
    margin: "0 auto 24px",
    boxShadow: "0 10px 28px rgba(0,0,0,0.35)",
  };

  const title = {
    color: "#ffffff",
    fontSize: "1.9rem",
    fontWeight: 800,
    textAlign: "center",
    margin: "6px 0 8px",
  };

  const subtitle = {
    color: colors.mut,
    fontSize: "1.05rem",
    textAlign: "center",
    marginBottom: 12,
  };

  const topBar = {
    position: "sticky",
    top: 0,
    zIndex: 20,
    padding: "10px 12px",
    background:
      "linear-gradient(180deg, rgba(15,15,15,0.95) 0%, rgba(15,15,15,0.9) 100%)",
    border: `1px solid ${colors.chipBorder}`,
    borderRadius: 12,
    backdropFilter: "blur(6px)",
  };

  const chip = (active) => ({
    display: "inline-block",
    padding: "8px 10px",
    margin: "4px 6px",
    borderRadius: 10,
    border: `1px solid ${active ? colors.border : colors.chipBorder}`,
    background: active ? "#22140e" : colors.chip,
    color: active ? colors.link : colors.text,
    fontSize: ".92rem",
    textDecoration: "none",
    cursor: "pointer",
    whiteSpace: "nowrap",
  });

  const sectionCard = {
    background: colors.panel,
    border: `1px solid ${colors.chipBorder}`,
    borderRadius: 12,
    padding: 16,
    marginBottom: 18,
  };

  const h3 = {
    fontSize: "1.3rem",
    margin: "0 0 10px",
    color: "#fff",
    fontWeight: 800,
  };

  const p = { color: colors.mut, lineHeight: 1.8, margin: "12px 0" };
  const ul = { margin: "10px 0 0 20px", color: colors.mut, lineHeight: 1.7 };
  const takeaways = {
    background: "#121212",
    border: `1px dashed ${colors.chipBorder}`,
    borderRadius: 10,
    padding: 12,
    marginTop: 12,
  };
  const backToTopWrap = { textAlign: "right", marginTop: 14 };

  // CONTENT
  // Friendly British English
  // Lots of line breaks
  // No long dashes or semicolons in prose
  const sections = useMemo(
    () => [
      {
        id: "focus",
        title: "Focus and Concentration",
        body: [
          `Chess is wonderful for focus because the board keeps asking for your full attention. You look at a fresh position and you can feel that even a small slip may change the story. That feeling is not scary. It is an invitation to slow down and give your best to the next move.`,

          `In daily life many of us juggle too much at once. We bounce between tabs and messages and it makes the mind noisy. On the chessboard you do one thing at a time. You scan for simple threats. You notice loose pieces. You count who controls a square. It feels honest and it calms the head.`,

          `If your thoughts wander you can bring them back with a tiny check. Ask three short questions. What is my opponent trying to do. What are my best ideas. What will change after each idea. This little habit keeps you present and it saves you from rushed moves.`,

          `You do not need perfection. You only need to be here for the next choice. Focus grows like a muscle. Short regular sessions are more helpful than long rare ones. Ten minutes of full attention can do more than an hour of half attention.`,

          `Children gain a lot from this. They learn to sit with a task without fear. They discover that quiet thinking can be enjoyable. Parents often see calmer homework and tidier writing. Teachers notice more patience and a nicer rhythm in lessons.`,

          `Adults feel benefits as well. After a few weeks of regular play you may find that meetings feel clearer. You listen better. You find the key point. You give a simple answer and you move on with less stress.`,

          `If your focus fades during a game try a friendly reset. Sit upright. Place both feet on the floor. Take one easy in breath and one slow out breath. Look for checks and captures first. Then choose a move. You will feel steady again.`,

          `Over time concentration becomes part of who you are. Not stiff. Not tense. Just present. Chess gives you many chances to practise that presence in a way that is playful and kind to the mind.`,
        ],
        bullets: [
          "Use a three question check before every move",
          "Short and regular practice grows focus best",
          "A small breathing reset brings calm in seconds",
        ],
      },
      {
        id: "critical-thinking",
        title: "Critical Thinking and Evaluation",
        body: [
          `Critical thinking in chess is very practical. You come up with an idea and then you test it. No drama and no ego. Just a curious look at what is true on the board. It feels a bit like a science lesson in the best sense. You form a small hypothesis and you try to break it before your opponent does.`,

          `A helpful start is to describe the position in plain words. Is my king safe. Do I control the centre. Where are the weak squares. Which pieces are not doing much. When you speak to yourself like this you avoid wishful thinking and you see the real picture in front of you.`,

          `Evaluation is your compass. You do not need to calculate every branch. If you know that your rooks want open files you look for a way to open them. If you know that a dark square is weak you bring a piece to that square. Small truths guide the plan and the plan guides the hand.`,

          `Good players also ask what they would play against themselves. This friendly habit keeps you honest. You look for counterplay you would rather not see. If your idea still stands after that check it is likely sound. If it falls apart you let it go without fuss and you try something new.`,

          `This way of thinking travels well into life. You read claims with a calm eye. You ask for proof. You test your first impression before you trust it. Chess is a daily chance to practise clear thinking in a safe and enjoyable setting.`,

          `When teaching young players it helps to celebrate good questions more than quick answers. A child who asks what the position is asking for is learning the heart of the game. The answer will come after and it will come with more confidence.`,
        ],
        bullets: [
          "Describe the position in simple language",
          "Use evaluation as a compass for plans",
          "Test your own ideas before the opponent does",
        ],
      },
      {
        id: "problem-solving",
        title: "Problem Solving and Creativity",
        body: [
          `Every chess position is a little puzzle. You will not know everything. You will not see every reply. That is fine. The first step is to define the problem in front of you. Are we attacking or defending. Do we need to trade to relieve pressure. Should we keep pieces to build an initiative. When the question is clear the answer becomes easier to find.`,

          `Creativity shows up when the usual path is blocked. Sometimes the best move is quiet. Sometimes you remove a defender and the whole position opens. Sometimes a small sacrifice frees your pieces and the king becomes shaky. It feels like magic when you first see it. With practice it feels natural because you have seen similar shapes before.`,

          `Tactical puzzles are a friendly gym for the brain. You learn patterns like forks and pins and discovered attacks. These patterns save time because your eyes know where to look. The more patterns you collect the more ideas appear in real games and the more fun the search becomes.`,

          `A neat trick is to imagine the position you want in a few moves. Picture your rook on an open file. Picture your knight on a strong square. Then work backwards and ask what must change now to make that picture real. This turns you from reactive to constructive and it feels good.`,

          `Another helpful idea is to play with gentle limits. Try to find only forcing moves for a minute. Then only quiet improving moves for a minute. Constraints like this reduce noise and free your mind to spot something fresh. Many lovely ideas appear when the field is narrower for a moment.`,

          `Off the board the same spirit helps with maths problems and with code bugs and with planning events. You define the problem. You list a few options. You test the options. You pick a path and you try it. Chess gives you a lot of friendly practice in that loop.`,
        ],
        bullets: [
          "Start by naming the real problem in the position",
          "Grow a pattern library with regular puzzles",
          "Work backwards from a dream position to today",
        ],
      },
      {
        id: "resilience",
        title: "Resilience and the Never Give Up Attitude",
        body: [
          `In chess you will make mistakes. Everyone does. The key is what you do next. Resilience is a calm choice to stay resourceful even when the position is worse. You keep looking for practical chances. You try to make your opponent find one more good move. You ask the board for that one resource that keeps the game alive.`,

          `Sometimes you can build a small fortress. Sometimes you can create checks that never end. Sometimes there is a stalemate idea hiding in a corner. Even if none of that appears you can still make the position as tricky as possible and you can learn to fight with a kind heart until the very end.`,

          `The language you use with yourself matters. Instead of saying that you are losing you can say that the position is tough and you will look for the next best move. That small change keeps your head clear and your hands steady. It turns a heavy feeling into a plan you can act on.`,

          `After the game resilience shows up again. You can review without blame. You can ask where your thinking slipped and what you can try next time. You do not attack yourself. You convert the sting into a lesson and you move forward lighter.`,

          `Children learn this quickly when the tone around them is warm. If adults praise effort and good habits the result hurts less. The child returns to the board the next day with the same joy. Adults benefit in the same way. Work and study bring hard moments and chess gives you training in how to meet them kindly.`,
        ],
        bullets: [
          "Look for practical chances in hard positions",
          "Use kind language with yourself to stay steady",
          "Turn painful results into simple next steps",
        ],
      },
      {
        id: "analysis",
        title: "Analysis Skills and Honest Review",
        body: [
          `Real improvement happens when you look back with gentle honesty. During a game you make choices with limited time. After a game you can slow the story down and see what really happened. The aim is not to prove that you were right. The aim is to understand how you thought and how you can think a bit better next time.`,

          `Start by writing your own notes without any engine. Mark the moments that felt important. Write your ideas and the lines you considered. This alone teaches a lot. You notice habits like rushing in sharp positions or avoiding trades without a clear reason. You see yourself more clearly and that is powerful.`,

          `Only after that bring in an engine as a second pair of eyes. Let it point out tactics you missed and moves that shift the balance. Then translate the engine line into a human story. Maybe the best move opened a file. Maybe it fixed a weak square. Give the move a reason you can remember.`,

          `Create a small map of mistakes that appear often. Maybe you overlook in between moves. Maybe your rook endings are shaky. Each tag becomes a tiny plan for the next week. Two focused themes beat a big list that never gets done.`,

          `End every review with a plan you can actually follow. A handful of tactical puzzles that match your theme. One annotated master game in your opening. One endgame drill until it feels easy. Small plans work because you do them. They build confidence and the next week you can add a little more.`,
        ],
        bullets: [
          "Self notes first then engine support",
          "Group repeat mistakes into simple themes",
          "Close each review with a tiny training plan",
        ],
      },
      {
        id: "memory",
        title: "Memory and Pattern Recognition",
        body: [
          `Chess memory is mostly about patterns rather than long lists of moves. You remember shapes and ideas and the way pieces work together. When a similar shape appears you feel a quiet bell ring in your head. That bell tells you where to look and it saves precious time on the clock.`,

          `Opening study works best when you learn stories. You learn where pieces belong and which pawn breaks lead to the positions you enjoy. If the move order changes you are still fine because the idea is alive in your mind. Flashcards can help but it is better to test understanding rather than raw recall.`,

          `Tactics training fills your head with small building blocks. Skewers and pins and forks and discovered attacks become familiar. Your eye catches them at once and you avoid blunders that used to feel invisible. Endgame study adds reliable tools as well. You learn simple techniques like opposition in king and pawn endings and classic rook ending ideas. These tools bring calm in the late phase of the game.`,

          `The same pattern sense helps in many fields. In coding you learn common shapes in problems and you reuse them. In language learning you notice phrases that appear again and again and you use them without effort. The more patterns you collect the easier each new task becomes.`,
        ],
        bullets: [
          "Learn ideas and plans not brittle move lists",
          "Use tactics to build fast pattern detection",
          "Know a few endgame techniques you can trust",
        ],
      },
      {
        id: "planning",
        title: "Planning and Strategic Foresight",
        body: [
          `A good plan connects what you have now with what you want a few moves later. Start by reading the position. Look at the pawn structure. Look at open files. Find your worst placed piece. When you see these simple facts a plan often suggests itself and it feels almost obvious.`,

          `Short term plans might be to improve a piece or to stop an opponent idea. Medium plans might be to place rooks on a file or to fix a weakness. Long plans might change the structure completely with a key pawn break. Strong players refresh the plan whenever the position changes. A trade or a file opening may mean the plan needs a small tweak.`,

          `You do not need to foresee everything. You only need a clear direction and one next step. Many games are won by steady small improvements rather than by one big idea. When you keep improving the pieces the position starts to play for you and tactics appear on their own.`,

          `This skill moves easily into life. You look at your week. You set a kind goal. You choose the next action that moves you towards it. Then you check again and adjust. Chess gives you a playful way to practise this rhythm and it is a lovely habit to keep.`,
        ],
        bullets: [
          "Read structure and piece activity before deciding",
          "Layer plans into short medium and long horizon",
          "Update the plan after every clear change",
        ],
      },
      {
        id: "emotion",
        title: "Emotional Regulation and Sportsmanship",
        body: [
          `Chess is logical but it is also emotional. The clock ticks. Pieces vanish. Mistakes happen. Emotional regulation is the art of feeling all of that and still choosing well. You separate the position from your identity. A tough position does not mean you are a poor player. It means you need one good move now.`,

          `Simple tools help in the hottest moments. One slow breath can turn a storm into a breeze. A tiny checklist can ground you. Checks and captures first. Loose pieces next. Then candidate moves. Write a small word on your scoresheet to remind yourself to stay present. It works for adults and for children too.`,

          `Sportsmanship matters because it keeps learning open. Shake hands. Say thank you. Review with a calm voice. You respect your opponent and you respect yourself. The result still matters yet it does not close your heart. This attitude builds communities that feel safe and warm.`,

          `Over time you carry these habits into school and work and family life. You notice stress rise and you have tools to bring it down. You respond instead of react. You keep relationships healthy while still playing to win on the board.`,
        ],
        bullets: [
          "Use simple breath and checklists in hot moments",
          "Keep reviews respectful to protect learning",
          "Respond rather than react under pressure",
        ],
      },
      {
        id: "learning",
        title: "Learning How to Learn",
        body: [
          `Some players grow quickly because they learn how to learn. They start with a gentle check of strengths and bottlenecks. Then they design short sessions that sit in the sweet spot. Not too easy. Not too hard. Just right for progress today.`,

          `A balanced week might include tactics for speed, annotated master games for strategy, and a touch of endgames for clean technique. Add a few real games to glue it all together. Short and regular beats long and rare. Spacing is your friend and your brain loves to revisit ideas at the right time.`,

          `Feedback makes everything faster. A coach or a study partner can see what you miss and keep you honest. Engines are helpful when used with care. Let them point to ideas and then ask what principle sits behind the move. Capture the principle. Leave the raw numbers behind.`,

          `Motivation rises when the path is clear. Prepare your space the night before. Keep a saved puzzle set. Celebrate small wins. One neat endgame. One clean defence. One calm choice in time trouble. These are the bricks that build confidence and joy.`,
        ],
        bullets: [
          "Design short sessions that match your level today",
          "Use human explanations first and engines as support",
          "Celebrate small wins to keep momentum",
        ],
      },
      {
        id: "social",
        title: "Social Skills Community and Joy",
        body: [
          `Chess is often seen as a quiet activity yet it is full of community. Clubs and teams and weekend events bring people together. You make friends across ages and backgrounds. You analyse games side by side and you help each other grow. It feels good to belong to something that rewards kindness and effort.`,

          `In team events you learn flexibility. Sometimes you attack freely. Sometimes you choose a solid draw for the team result. You practise giving feedback with care. You speak clearly and you listen well. Juniors thrive in this because they feel seen and useful and they learn to support others too.`,

          `Chess can stay with you for life because it adjusts to your season. You can enjoy a quick game on your phone in a café. You can play a slow league match once a week. You can solve a puzzle before bed. The game meets you where you are and that makes it a steady source of growth and comfort.`,

          `Communities that welcome beginners and celebrate curiosity are the ones that keep people coming back. A simple code of conduct. A friendly hello at the door. A smile when someone blunders and a kind word after. These small touches turn a hard game into a human experience.`,

          `Many parents tell us they came for the chess and stayed for the people. That is a lovely compliment and a reminder that improvement and joy can sit together.`,
        ],
        bullets: [
          "Practise clear speaking and kind listening",
          "Enjoy friendships across ages and levels",
          "Let chess fit your life from quick blitz to calm classics",
        ],
      },
    ],
    []
  );

  // SCROLL SPY
  const refs = useRef({});
  const [active, setActive] = useState(sections[0].id);

  useEffect(() => {
    const root = document.documentElement;
    const prev = root.style.scrollBehavior;
    root.style.scrollBehavior = "smooth";

    sections.forEach((s) => {
      if (!refs.current[s.id]) refs.current[s.id] = React.createRef();
    });

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) {
          const id = visible[0].target.getAttribute("data-id");
          if (id) setActive(id);
        }
      },
      { threshold: [0.2, 0.35, 0.6], rootMargin: "-10% 0px -70% 0px" }
    );

    sections.forEach((s) => {
      const node = refs.current[s.id]?.current;
      if (node) observer.observe(node);
    });

    return () => {
      observer.disconnect();
      root.style.scrollBehavior = prev;
    };
  }, [sections]);

  const handleJump = (id) => (e) => {
    e.preventDefault();
    const node = refs.current[id]?.current;
    if (node) node.scrollIntoView({ behavior: "smooth", block: "start" });
    setActive(id);
    history.replaceState(null, "", `#${id}`);
  };

  const handleTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setActive(sections[0].id);
    history.replaceState(null, "", `#`);
  };

  const chipsRow = (
    <div style={topBar} aria-label="Table of contents">
      {sections.map((s, idx) => (
        <a
          key={s.id}
          href={`#${s.id}`}
          onClick={handleJump(s.id)}
          style={chip(active === s.id)}
          aria-label={`Jump to ${s.title}`}
        >
          <Badge bg="" style={{ marginRight: 6, border: "1px solid #333" }}>
            {idx + 1}
          </Badge>
          {s.title}
        </a>
      ))}
      <span style={{ float: "right", marginTop: 6 }}>
        <Button size="sm" variant="outline-light" onClick={handleTop}>
          Back to top
        </Button>
      </span>
    </div>
  );

  return (
    <div style={shell}>
      <Container fluid className="p-0">
        <h2 style={title}>The Benefits of Chess for Children and Adults</h2>

        <div style={subtitle}>
          Friendly thoughts and real world tips you can use today
        </div>

        {chipsRow}

        <Row className="g-3 mt-2">
          <Col xs={12}>
            {sections.map((s) => (
              <section
                key={s.id}
                id={s.id}
                data-id={s.id}
                ref={refs.current[s.id]}
                style={sectionCard}
                aria-labelledby={`${s.id}-title`}
              >
                <h3 id={`${s.id}-title`} style={h3}>
                  {s.title}
                </h3>

                <p style={{ ...p, color: colors.dim, marginTop: -4 }}>
                  Section • <a href={`#${s.id}`}>#{s.id}</a>
                </p>

                {s.body.map((para, i) => (
                  <p key={i} style={p}>
                    {para}
                  </p>
                ))}

                {s.bullets?.length ? (
                  <div style={takeaways}>
                    <strong>Key ideas to try</strong>
                    <ul style={ul}>
                      {s.bullets.map((b, i) => (
                        <li key={i}>{b}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <div style={backToTopWrap}>
                  <Button size="sm" variant="outline-light" onClick={handleTop}>
                    Back to top
                  </Button>
                </div>
              </section>
            ))}
          </Col>
        </Row>

        {chipsRow}
      </Container>
    </div>
  );
}
